/**
 * Échéances personnalisées — M6 (retour Manon, mi-parcours).
 *
 * « Créer son échéance personnalisable » : un titre, une date, jusqu'à deux
 * rappels (« 1 mois avant », « 7 jours avant »), des destinataires choisis dans
 * l'annuaire. Le jour J est toujours prévenu. « C'est fait » arrête les rappels.
 * Affichée dans « Mon dossier personnel » (tout le personnel) et dans
 * Administration → Échéances (les admins y voient celles de toute la commune).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarPlus, Check, X } from 'lucide-react';
import { Card, Button, Input, Badge } from '@/components/ui';
import { api, ApiException, type EcheancePersoDTO } from '@/lib/api';
import type { AgentAnnuaire } from '@roronoa-collectivites/shared-types';

type Unite = 'jours' | 'mois' | 'annees';
const LIB_UNITE: Record<Unite, string> = { jours: 'jour(s)', mois: 'mois', annees: 'an(s)' };
const dateFr = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

export function EcheancesPersoCard({ titre = 'Mes échéances personnalisées' }: { titre?: string }) {
  const [items, setItems] = useState<EcheancePersoDTO[]>([]);
  const [annuaire, setAnnuaire] = useState<AgentAnnuaire[]>([]);
  const [form, setForm] = useState(false);
  const [t, setT] = useState(''); const [date, setDate] = useState(''); const [desc, setDesc] = useState('');
  const [rappels, setRappels] = useState<Array<{ quantite: number; unite: Unite }>>([{ quantite: 7, unite: 'jours' }]);
  const [dests, setDests] = useState<string[]>([]);
  const [filtre, setFiltre] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try { setItems(await api.echeancesPerso.lister()); } catch { setItems([]); }
  }, []);
  useEffect(() => {
    void charger();
    api.annuaireInterne.lister().then((r) => setAnnuaire(r.agents)).catch(() => setAnnuaire([]));
  }, [charger]);

  const visibles = useMemo(() => {
    const f = filtre.trim().toLowerCase();
    return annuaire.filter((a) => !f || `${a.prenom} ${a.nom}`.toLowerCase().includes(f)).slice(0, 40);
  }, [annuaire, filtre]);

  async function creer() {
    if (t.trim().length < 3 || !date) { setErr('Un titre (3 caractères au moins) et une date sont nécessaires'); return; }
    setEnvoi(true); setErr(null);
    try {
      await api.echeancesPerso.creer({ titre: t.trim(), description: desc.trim() || null, dateEcheance: date, rappels, destinataireIds: dests });
      setForm(false); setT(''); setDate(''); setDesc(''); setDests([]); setRappels([{ quantite: 7, unite: 'jours' }]);
      await charger();
    } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Création impossible'); }
    finally { setEnvoi(false); }
  }

  async function terminer(id: string, action: 'faite' | 'annuler') {
    try { await (action === 'faite' ? api.echeancesPerso.faite(id) : api.echeancesPerso.annuler(id)); await charger(); }
    catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Action impossible'); }
  }

  const aujourdhui = new Date().toISOString().slice(0, 10);

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <CalendarPlus className="h-5 w-5 text-rh" aria-hidden="true" />
        <h2 className="font-display text-base font-semibold text-ink">{titre}</h2>
        {!form && <Button size="sm" variant="rh" className="ml-auto" onClick={() => setForm(true)}>Nouvelle échéance</Button>}
      </div>
      {err && <p className="mb-2 text-xs text-urgent" role="alert">{err}</p>}

      {form && (
        <div className="mb-4 space-y-3 rounded-r-lg border border-border/60 p-3">
          <label className="block text-xs text-ink">Titre
            <Input className="mt-1" value={t} maxLength={200} placeholder="Ex : renouveler la licence du logiciel de paie" onChange={(e) => setT(e.target.value)} />
          </label>
          <label className="block text-xs text-ink">Date de l'échéance
            <Input className="mt-1" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <fieldset>
            <legend className="text-xs text-ink">Me prévenir (en plus du jour même)</legend>
            {rappels.map((r, i) => (
              <div key={i} className="mt-1 flex items-center gap-2 text-sm">
                <Input type="number" min={1} max={999} className="w-20" aria-label={`Rappel ${i + 1} : quantité`} value={r.quantite}
                  onChange={(e) => setRappels((l) => l.map((x, k) => (k === i ? { ...x, quantite: Math.max(1, Number(e.target.value) || 1) } : x)))} />
                <select aria-label={`Rappel ${i + 1} : unité`} value={r.unite} className="h-9 rounded-r-md border border-border bg-surface px-2 text-sm"
                  onChange={(e) => setRappels((l) => l.map((x, k) => (k === i ? { ...x, unite: e.target.value as Unite } : x)))}>
                  {(Object.keys(LIB_UNITE) as Unite[]).map((u) => <option key={u} value={u}>{LIB_UNITE[u]}</option>)}
                </select>
                <span className="text-ink-muted">avant</span>
                <button type="button" aria-label={`Retirer le rappel ${i + 1}`} className="text-ink-muted hover:text-urgent"
                  onClick={() => setRappels((l) => l.filter((_, k) => k !== i))}><X className="h-4 w-4" /></button>
              </div>
            ))}
            {rappels.length < 2 && (
              <button type="button" className="mt-1 text-xs text-accent hover:underline"
                onClick={() => setRappels((l) => [...l, { quantite: 1, unite: 'mois' }])}>+ Ajouter un rappel</button>
            )}
          </fieldset>
          <fieldset>
            <legend className="text-xs text-ink">Prévenir aussi (vous l'êtes toujours)</legend>
            <Input className="mt-1" value={filtre} placeholder="Filtrer par nom…" aria-label="Filtrer l'annuaire" onChange={(e) => setFiltre(e.target.value)} />
            <div className="mt-2 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
              {visibles.map((a) => {
                const choisi = dests.includes(a.id);
                return (
                  <button key={a.id} type="button" aria-pressed={choisi}
                    onClick={() => setDests((l) => (choisi ? l.filter((x) => x !== a.id) : [...l, a.id]))}
                    className={`inline-flex h-8 items-center rounded-r-pill border px-3 text-xs ${choisi ? 'border-rh/50 bg-rh/10 font-medium text-ink' : 'border-border bg-surface/50 text-ink-muted'}`}>
                    {a.prenom} {a.nom}
                  </button>
                );
              })}
            </div>
          </fieldset>
          <label className="block text-xs text-ink">Note (facultatif)
            <Input className="mt-1" value={desc} maxLength={2000} placeholder="Ce qu'il faudra faire ce jour-là" onChange={(e) => setDesc(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <Button size="sm" variant="rh" loading={envoi} onClick={() => void creer()}>Créer l'échéance</Button>
            <Button size="sm" variant="ghost" onClick={() => setForm(false)}>Annuler</Button>
          </div>
        </div>
      )}

      {items.length === 0 && !form && <p className="text-sm text-ink-subtle">Aucune échéance à venir.</p>}
      <ul className="space-y-2">
        {items.map((e) => (
          <li key={e.id} className="rounded-r-md border border-border/60 px-3 py-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-ink">{e.titre}</span>
              <Badge variant={e.dateEcheance < aujourdhui ? 'urgent' : 'neutral'} size="sm">{dateFr(e.dateEcheance)}</Badge>
              <span className="ml-auto flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => void terminer(e.id, 'faite')}><Check className="h-4 w-4" aria-hidden="true" /> C'est fait</Button>
                <Button size="sm" variant="ghost" onClick={() => void terminer(e.id, 'annuler')} aria-label={`Annuler l'échéance ${e.titre}`}><X className="h-4 w-4" aria-hidden="true" /></Button>
              </span>
            </div>
            <p className="text-xs text-ink-muted">
              {e.rappels.length > 0 ? `Rappels : ${e.rappels.map((r) => `${r.quantite} ${LIB_UNITE[r.unite]} avant`).join(', ')}, et le jour même` : 'Rappel le jour même'}
              {e.destinataires.length > 0 ? ` · prévient aussi ${e.destinataires.map((d) => d.nom).join(', ')}` : ''}
              {e.creeParNom ? ` · créée par ${e.creeParNom}` : ''}
            </p>
            {e.description && <p className="text-xs text-ink-subtle">{e.description}</p>}
          </li>
        ))}
      </ul>
    </Card>
  );
}
